import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router';
import type { FormEvent } from 'react';

import { Button } from '@/components/Button/Button';

import { ProfileField } from './ProfileField';
import { ThemeSelector } from './ThemeSelector';
import { useProfile } from './useProfile';
import styles from './ProfilePage.module.css';

const INSTITUTE_ID_HELP =
  'This should be the unique ID assigned by your university/institute that your instructor will use to match your account to university records (e.g., A13579123).';
const PREFERRED_EMAIL_HELP =
  'This is the email that will be displayed in the course roster. You will receive any communication from Kudu on this email.';

export function ProfilePage() {
  const navigate = useNavigate();
  const {
    canSave,
    cancelEditing,
    draft,
    error,
    formStatusMessage,
    isEditing,
    isLoading,
    isSaving,
    profile,
    refetch,
    saveProfile,
    startEditing,
    updateField,
    validationErrors,
  } = useProfile();

  function handleBack() {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/app/courses');
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void saveProfile();
  }

  if (isLoading) {
    return (
      <section aria-busy="true" className={styles.page}>
        <p className={styles.stateMessage}>Loading your profile...</p>
      </section>
    );
  }

  if (error && !profile) {
    return (
      <section className={styles.page}>
        <div className={styles.stateCard} role="alert">
          <h1 className={styles.title}>Profile</h1>
          <p className={styles.stateMessage}>{error.message}</p>
          <Button onClick={() => void refetch()} variant="secondary">
            Try again
          </Button>
        </div>
      </section>
    );
  }

  if (!profile) {
    return (
      <section className={styles.page}>
        <div className={styles.stateCard} role="alert">
          <h1 className={styles.title}>Profile</h1>
          <p className={styles.stateMessage}>Profile data is not available.</p>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.page}>
      <button className={styles.backButton} onClick={handleBack} type="button">
        <ArrowLeft aria-hidden="true" size={18} />
        Back
      </button>

      <form className={styles.form} noValidate onSubmit={handleSubmit}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Profile</h1>
            <p className={styles.subtitle}>
              Keep your account details accurate for your courses.
            </p>
          </div>

          <div className={styles.actions}>
            {isEditing ? (
              <>
                <Button
                  disabled={isSaving}
                  onClick={cancelEditing}
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button disabled={!canSave} type="submit">
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <Button onClick={startEditing} variant="secondary">
                Edit
              </Button>
            )}
          </div>
        </div>

        <div className={styles.card}>
          {profile.loginEmail ? (
            <ProfileField
              id="profile-login-email"
              isEditing={isEditing}
              label="Login email"
              readOnly
              value={profile.loginEmail}
            />
          ) : null}

          <ProfileField
            autoComplete="email"
            error={isEditing ? validationErrors.preferredEmail : undefined}
            id="profile-preferred-email"
            inputMode="email"
            isEditing={isEditing}
            label="Preferred Email"
            onChange={(value) => updateField('preferredEmail', value)}
            tooltipContent={PREFERRED_EMAIL_HELP}
            type="email"
            value={isEditing ? draft.preferredEmail : profile.preferredEmail}
          />

          <ProfileField
            autoComplete="given-name"
            error={isEditing ? validationErrors.firstName : undefined}
            id="profile-first-name"
            isEditing={isEditing}
            label="First name"
            onChange={(value) => updateField('firstName', value)}
            value={isEditing ? draft.firstName : profile.firstName}
          />

          <ProfileField
            autoComplete="family-name"
            error={isEditing ? validationErrors.lastName : undefined}
            id="profile-last-name"
            isEditing={isEditing}
            label="Last name"
            onChange={(value) => updateField('lastName', value)}
            value={isEditing ? draft.lastName : profile.lastName}
          />

          <ProfileField
            id="profile-institute-id"
            isEditing={isEditing}
            label="Student/Institute ID"
            onChange={(value) => updateField('instituteOfficialId', value)}
            tooltipContent={INSTITUTE_ID_HELP}
            value={
              isEditing
                ? draft.instituteOfficialId
                : profile.instituteOfficialId
            }
          />
        </div>

        <p aria-live="polite" className={styles.liveRegion}>
          {isEditing || isSaving ? formStatusMessage : ''}
        </p>
      </form>

      <section aria-labelledby="profile-preferences-title" className={styles.preferences}>
        <div className={styles.preferencesHeader}>
          <h2 className={styles.sectionTitle} id="profile-preferences-title">
            Preferences
          </h2>
          <p className={styles.sectionDescription}>
            Personalize how Kudu looks on this device and across sessions.
          </p>
        </div>
        <div className={styles.card}>
          <ThemeSelector />
        </div>
      </section>
    </section>
  );
}
